import React from "react";
import { connect } from "react-redux";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import loadingAction from "../../core/redux/actions/loadingAction";
import strings from "../../core/translate";
import PageHeader from "../../components/Page/PageHeader";
import EmptyState from "../../components/Page/EmptyState";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import { apiErrorMessage } from "../../shared/api-error";
import { formatMileage, mileageDigits } from "../../shared/mileage";
import {
  FormSection,
  FormGrid,
  TextField,
  SelectField,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";
import "../../assets/css/job-cards.css";

// The same limits the app works to: one walk-around clip, up to ten stills.
const MAX_PHOTOS = 10;
const MAX_VIDEO_BYTES = 64 * 1024 * 1024;

// Where the fuel needle sits, in quarters of the tank.
const FUEL_MARKS = ["E", "¼", "½", "¾", "F"];

/**
 * Receiving a car into the garage, from the web.
 *
 * This is what "start service" means: the garage takes the keys, writes down
 * the odometer and what the customer said, and the visit becomes a job card.
 * Opening the card is what moves the appointment to "in progress" — the same
 * rule the worker app follows — so there is no separate button that only
 * flips a status and leaves no record of the car actually arriving.
 *
 * The customer and the vehicle are not editable here: they come from the
 * appointment, and a garage receiving a car is recording what turned up, not
 * choosing it.
 */
class JobCardAdd extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      booking: null,
      branchName: "",
      merchantId: "",
      workers: [],
      workerId: "",
      mileage: "",
      lastMileage: null,
      lastMileageDate: null,
      dropOffNotes: "",
      // the fuel needle, 0 (empty) to 100 (full)
      fuelLevel: 50,
      deliveryMethods: [],
      deliveryMethodId: "",
      // the driver's name and phone, or the recovery company
      contactName: "",
      contactPhone: "",
      // the walk-around video is required, the photos are not
      video: null,
      photos: [],
      // set once the card exists, so a failed upload can't open it twice
      createdId: null,
      saving: false,
      error: "",
      errors: {},
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.load();
  }

  get branchId() {
    return this.props.match.params.selectedBranch;
  }

  get bookingId() {
    return this.props.match.params.bookingId;
  }

  fetchList = async (path) => {
    try {
      const response = await this.engine.getItem(path);
      if (!response || response.status !== 200) return [];
      const data = response.data;
      return Array.isArray(data) ? data : data.items || [];
    } catch (err) {
      return [];
    }
  };

  load = async () => {
    const { branchId, bookingId } = this;

    // The booking is read from the api rather than carried in route state, so
    // the page survives a refresh and a pasted link.
    const bookings = await this.fetchList(
      `merchants-branches/${branchId}/bookings`
    );
    const booking = bookings.find((item) => item.id === bookingId) || null;

    const branchResponse = await this.engine
      .getItem(`merchants-branches/${branchId}`)
      .catch(() => null);
    const branch = branchResponse && branchResponse.data;
    const merchantId = branch && branch.merchant && branch.merchant.id;

    // Staff are listed per merchant, so narrow them to this garage: a card has
    // to be filed against somebody who works here.
    const staff = merchantId
      ? await this.fetchList(`merchants/${merchantId}/workers`)
      : [];
    // only the active ways in are offered, in the admin's order
    const deliveryMethods = (
      await this.fetchList("delivery-methods?isActive=true")
    ).map((method) => ({
      value: method.id,
      label: method.name,
      // what it asks for: none, a driver (person) or a company
      details: method.details || "none",
    }));

    // Receiving a car is an Owner's or Manager's job, as in the worker app.
    const workers = staff
      .filter((worker) => worker.branch && worker.branch.id === branchId)
      .filter((worker) => worker.role !== "mechanic")
      .map((worker) => ({
        value: worker.id,
        label: worker.title ? `${worker.name} — ${worker.title}` : worker.name,
      }));

    this.setState({
      isLoading: false,
      booking,
      branchName: (branch && (branch.name || branch.address)) || "",
      // the section header's back link needs it to reach the garages list
      merchantId: merchantId || "",
      workers,
      workerId: workers.length === 1 ? workers[0].value : "",
      deliveryMethods,
    });

    if (booking && booking.vehicleId) this.loadLastMileage(booking.vehicleId);
    this.props.loadingAction(false);
  };

  /**
   * The odometer at this car's last visit. Not prefilled — the reading has to
   * be the one on the dial today — but shown, because it is the number that
   * makes a typo obvious.
   */
  loadLastMileage = async (vehicleId) => {
    const cards = await this.fetchList(
      `merchants-branches/${this.branchId}/job-cards`
    );
    const previous = cards
      .filter((card) => card.customerVehicleId === vehicleId)
      .sort((a, b) => `${b.createdAt}`.localeCompare(`${a.createdAt}`))[0];
    if (previous && (previous.mileage || previous.mileage === 0)) {
      this.setState({
        lastMileage: previous.mileage,
        lastMileageDate: previous.createdAt || null,
      });
    }
  };

  /** "Mar 4, 2026", the same short form the worker app shows next to it. */
  formatLastMileageWhen = (rawDate) => {
    if (!rawDate) return "";
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return "";
    return ` · ${date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })}`;
  };

  /** What the picked delivery method asks for. */
  get contactDetails() {
    const method = this.state.deliveryMethods.find(
      (m) => m.value === this.state.deliveryMethodId
    );
    return (method && method.details) || "none";
  }

  validate() {
    const { workerId, mileage, deliveryMethodId, video, contactName, contactPhone } =
      this.state;
    const errors = {};
    const details = this.contactDetails;
    if (details !== "none" && !`${contactName}`.trim()) {
      errors.contactName = strings.required_field;
    }
    if (details === "person" && !`${contactPhone}`.trim()) {
      errors.contactPhone = strings.required_field;
    }

    if (!workerId) errors.workerId = strings.select;
    if (`${mileage}`.trim() === "" || Number(mileage) < 0) {
      errors.mileage = strings.job_card_mileage_hint;
    }
    if (!deliveryMethodId) errors.deliveryMethodId = strings.select;
    if (!video) errors.video = strings.job_card_video_required;
    return errors;
  }

  pickVideo = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) {
      this.setState({
        errors: { ...this.state.errors, video: strings.job_card_video_too_big },
      });
      return;
    }
    this.setState({ video: file, errors: { ...this.state.errors, video: "" } });
  };

  pickPhotos = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    this.setState((state) => ({
      photos: [...state.photos, ...files].slice(0, MAX_PHOTOS),
    }));
  };

  removePhoto = (index) =>
    this.setState((state) => ({
      photos: state.photos.filter((_, i) => i !== index),
    }));

  /**
   * Sends the video, then the photos, to a card that now exists. Returns how
   * many failed — the card stays open either way, and the app can add a
   * missing file later.
   */
  uploadMedia = async (jobCardId) => {
    const { video, photos } = this.state;
    let failed = 0;
    for (const file of [video, ...photos].filter(Boolean)) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await this.engine.postItemMultipart(
          `merchants-branches/${this.branchId}/job-cards/${jobCardId}/photos`,
          form
        );
        if (!res || (res.status !== 200 && res.status !== 201)) failed += 1;
      } catch (err) {
        failed += 1;
      }
    }
    return failed;
  };

  goToJobCards = () =>
    this.props.history.push(
      `/admin/merchants-branches/${this.branchId}/job-cards`
    );

  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const {
      booking,
      workerId,
      mileage,
      dropOffNotes,
      fuelLevel,
      deliveryMethodId,
    } = this.state;
    this.setState({ saving: true, error: "", errors: {} });

    try {
      const response = await this.engine.saveItem(
        `merchants-branches/${this.branchId}/job-cards`,
        {
          customerVehicleId: booking.vehicleId,
          bookingId: booking.id,
          workerId,
          mileage: Number(mileage),
          fuelLevel: Math.round(Number(fuelLevel)),
          deliveryMethodId,
          ...(this.contactDetails !== "none" && {
            deliveryContactName: `${this.state.contactName}`.trim(),
          }),
          ...(this.contactDetails === "person" && {
            deliveryContactPhone: `${this.state.contactPhone}`.trim(),
          }),
          ...(dropOffNotes.trim() && { dropOffNotes: dropOffNotes.trim() }),
        }
      );

      if (response && (response.status === 200 || response.status === 201)) {
        const createdId = response.data && response.data.id;
        this.setState({ createdId });
        const failed = createdId ? await this.uploadMedia(createdId) : 0;
        if (failed > 0) {
          this.setState({
            saving: false,
            error: strings.formatString(
              strings.job_card_media_failed,
              failed
            ),
          });
          return;
        }
        // Straight to the garage's job cards: the card now exists and the
        // list is where the rest of the visit is worked on.
        this.goToJobCards();
        return;
      }
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
    this.setState({ saving: false });
  };

  handleCancel = () => {
    if (this.props.history.length > 1) {
      this.props.history.goBack();
      return;
    }
    this.props.history.push("/admin/dashboard");
  };

  renderSummary(booking) {
    const rows = [
      {
        key: "appointment",
        icon: "nc-icon nc-calendar-60",
        label: strings.appointment,
        value: [booking.appointmentDate, booking.appointmentTime]
          .filter(Boolean)
          .join(" · "),
      },
      {
        key: "customer",
        icon: "nc-icon nc-single-02",
        label: strings.customer,
        value: [booking.customerName, booking.customerPhone]
          .filter(Boolean)
          .join(" · "),
      },
      {
        key: "vehicle",
        icon: "nc-icon nc-delivery-fast",
        label: strings.vehicle,
        value:
          [booking.vehicleMake, booking.vehicleModel, booking.vehicleYear]
            .filter(Boolean)
            .join(" ") || "—",
      },
      {
        key: "plate",
        icon: "nc-icon nc-tag-content",
        label: strings.platenumber,
        value: booking.plateNumber || "—",
        strong: true,
      },
    ];

    return (
      <div className="panel mb-3">
        <div className="job-card-summary">
          {rows.map((row) => (
            <div className="job-card-detail" key={row.key}>
              <span className="job-card-detail__label">
                <i className={row.icon} /> {row.label}
              </span>
              <span
                className={`job-card-detail__value${
                  row.strong ? " is-strong" : ""
                }`}
              >
                {row.value || "—"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const {
      isLoading,
      booking,
      branchName,
      merchantId,
      workers,
      workerId,
      mileage,
      lastMileage,
      lastMileageDate,
      dropOffNotes,
      fuelLevel,
      deliveryMethods,
      deliveryMethodId,
      video,
      photos,
      createdId,
      saving,
      error,
      errors,
    } = this.state;

    if (isLoading) return this.renderProgress();

    // Odometers don't run backwards: a reading under the last one on file is
    // almost always a typo, so it gets called out the same way the worker
    // app calls it out, instead of only showing the number.
    const enteredMileage = mileage === "" ? NaN : Number(mileage);
    const isLowerMileage =
      lastMileage !== null &&
      !Number.isNaN(enteredMileage) &&
      enteredMileage > 0 &&
      enteredMileage < lastMileage;

    return (
      <div className="content">
        <GarageSectionHeader
          branchId={this.branchId}
          branchName={branchName}
          merchantId={merchantId}
          active="job-cards"
          history={this.props.history}
        />

        <PageHeader
          icon="nc-icon nc-paper"
          title={strings.new_job_card}
          subtitle={strings.new_job_card_hint}
        />

        {!booking ? (
          <EmptyState
            icon="nc-icon nc-alert-circle-i"
            title={strings.booking_not_found}
          />
        ) : (
          <>
            {this.renderSummary(booking)}

            <div className="panel">
              <form className="mform" onSubmit={this.handleSubmit}>
                <FormSection
                  title={strings.new_job_card}
                  icon="nc-icon nc-paper"
                >
                  <FormGrid>
                    <SelectField
                      label={strings.job_card_received_by}
                      required
                      value={workerId}
                      options={workers}
                      onChange={(value) => this.setState({ workerId: value })}
                      help={
                        workers.length === 0
                          ? strings.no_workers_hint
                          : strings.job_card_received_by_hint
                      }
                      error={errors.workerId}
                      disabled={workers.length === 0}
                    />
                    <TextField
                      label={strings.arrivalmileage}
                      inputMode="numeric"
                      required
                      value={formatMileage(mileage)}
                      onChange={(value) =>
                        this.setState({ mileage: mileageDigits(value) })
                      }
                      help={
                        lastMileage === null ? (
                          strings.job_card_mileage_hint
                        ) : isLowerMileage ? (
                          <span className="job-card-mileage-hint is-warning">
                            <i className="nc-icon nc-alert-circle-i" />{" "}
                            {strings.formatString(
                              strings.job_card_mileage_lower_warning,
                              formatMileage(lastMileage)
                            )}
                          </span>
                        ) : (
                          <span className="job-card-mileage-hint">
                            <i className="nc-icon nc-watch-time" />{" "}
                            {strings.formatString(
                              strings.job_card_last_mileage,
                              formatMileage(lastMileage),
                              this.formatLastMileageWhen(lastMileageDate)
                            )}
                          </span>
                        )
                      }
                      error={errors.mileage}
                    />
                  </FormGrid>

                  <FormGrid>
                    <div className="job-card-fuel">
                      <span className="job-card-fuel__label">
                        {strings.fuel_level}
                        <strong>{Math.round(fuelLevel)}%</strong>
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={fuelLevel}
                        className="job-card-fuel__range"
                        style={{ "--fuel": `${fuelLevel}%` }}
                        onChange={(e) =>
                          this.setState({ fuelLevel: Number(e.target.value) })
                        }
                        aria-label={strings.fuel_level}
                      />
                      <div className="job-card-fuel__marks">
                        {FUEL_MARKS.map((mark) => (
                          <span key={mark}>{mark}</span>
                        ))}
                      </div>
                    </div>
                    <SelectField
                      label={strings.delivery_method}
                      required
                      value={deliveryMethodId}
                      options={deliveryMethods}
                      onChange={(value) =>
                        this.setState({ deliveryMethodId: value })
                      }
                      help={
                        deliveryMethods.length === 0
                          ? strings.no_delivery_methods_hint
                          : strings.delivery_method_hint
                      }
                      error={errors.deliveryMethodId}
                      disabled={deliveryMethods.length === 0}
                    />
                  </FormGrid>

                  {this.contactDetails !== "none" && (
                    <FormGrid>
                      <TextField
                        label={
                          this.contactDetails === "person"
                            ? strings.driver_name
                            : strings.recovery_company
                        }
                        required
                        value={this.state.contactName}
                        onChange={(value) =>
                          this.setState({ contactName: value })
                        }
                        error={errors.contactName}
                      />
                      {this.contactDetails === "person" && (
                        <TextField
                          label={strings.driver_phone}
                          required
                          value={this.state.contactPhone}
                          onChange={(value) =>
                            this.setState({ contactPhone: value })
                          }
                          error={errors.contactPhone}
                        />
                      )}
                    </FormGrid>
                  )}

                  <div className="job-card-media">
                    <div className="job-card-media__group">
                      <span className="job-card-media__label">
                        {strings.job_card_video}
                        <span className="job-card-media__req">*</span>
                      </span>
                      {video ? (
                        <span className="job-card-media__file">
                          <i className="nc-icon nc-button-play" /> {video.name}
                          <button
                            type="button"
                            className="job-card-media__remove"
                            onClick={() => this.setState({ video: null })}
                            aria-label={strings.delete}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <label className="job-card-media__pick">
                          <i className="nc-icon nc-button-play" />{" "}
                          {strings.add_video}
                          <input
                            type="file"
                            accept="video/*"
                            onChange={this.pickVideo}
                            hidden
                          />
                        </label>
                      )}
                      <span
                        className={`job-card-media__help${
                          errors.video ? " is-error" : ""
                        }`}
                      >
                        {errors.video || strings.job_card_video_hint}
                      </span>
                    </div>

                    <div className="job-card-media__group">
                      <span className="job-card-media__label">
                        {strings.photos}{" "}
                        <span className="job-card-media__count">
                          {photos.length}/{MAX_PHOTOS}
                        </span>
                      </span>
                      <div className="job-card-media__files">
                        {photos.map((file, index) => (
                          <span
                            className="job-card-media__file"
                            key={`${file.name}-${index}`}
                          >
                            <i className="nc-icon nc-image" /> {file.name}
                            <button
                              type="button"
                              className="job-card-media__remove"
                              onClick={() => this.removePhoto(index)}
                              aria-label={strings.delete}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {photos.length < MAX_PHOTOS && (
                          <label className="job-card-media__pick">
                            <i className="nc-icon nc-image" />{" "}
                            {strings.add_photos}
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={this.pickPhotos}
                              hidden
                            />
                          </label>
                        )}
                      </div>
                      <span className="job-card-media__help">
                        {strings.job_card_photos_hint}
                      </span>
                    </div>
                  </div>

                  <FormGrid full>
                    <TextField
                      label={strings.job_card_notes}
                      type="textarea"
                      value={dropOffNotes}
                      onChange={(value) =>
                        this.setState({ dropOffNotes: value })
                      }
                      help={strings.job_card_notes_hint}
                    />
                  </FormGrid>
                </FormSection>

                <FormAlert>{error}</FormAlert>
                {createdId && !saving ? (
                  // the card exists; a file failed — move on rather than
                  // opening it a second time
                  <div className="mform__footer">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={this.goToJobCards}
                    >
                      {strings.go_to_job_cards}
                    </button>
                  </div>
                ) : (
                  <FormActions
                    onCancel={this.handleCancel}
                    loading={saving}
                    saveLabel={strings.open_job_card}
                  />
                )}
              </form>
            </div>
          </>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(JobCardAdd);
